import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, connect } from 'react-redux';
import { Search } from 'carbon-components-react';
import ResultsBox from './resultsBox';
import {
  MAP_DATA_SERVICE_DATA_SET,
  CFCProps,
  MapGetPlaceSuggestPayload
} from 'types';
import Util from 'utilities/helper.util';
import {
  getAddressSearchSuggestStart,
  clearAddressSearchSuggest
} from 'redux/user/actions';
import { addAlert, removeAlertByGroup } from 'redux/alerts/actions';
import { RootState } from 'redux/root.reducer';
import { useEventListener, useComponentMount } from 'utilities/hooks.util';
import { optimizeLabel } from './helper';

const AddressSearchPanel: React.FC<Props> = ({
  suggestion,
  labelText,
  defaultInputValue = '',
  onChangeInput,
  onBlurInput
}) => {
  const [searchInputValue, setSearchInputValue] = useState<string>(
    defaultInputValue
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [isShowResult, setIsShowResult] = useState<Boolean>(false);
  const dispatch = useDispatch();
  const debouncedFunction = useCallback(
    Util.debounce(async (args: string[]) => {
      const value = args[0];
      const payload: MapGetPlaceSuggestPayload = {
        datasets: MAP_DATA_SERVICE_DATA_SET.GNAF_ADDRESS,
        query: value,
        fields: 'region,locality',
        limit: '5'
      };
      dispatch(getAddressSearchSuggestStart(payload));
    }, 500),
    []
  );

  useEffect(() => {
    if (typeof suggestion === 'string') {
      dispatch(addAlert('address-search-alert', 'error', 'Error', suggestion));
    } else {
      setIsShowResult(Array.isArray(suggestion) && suggestion.length > 0);
    }
  }, [suggestion, dispatch]);

  const onClickResult = (item: any) => {
    if (item.Value) {
      const label = optimizeLabel(item.Value, item.CustomFields);
      setSearchInputValue(label);
      typeof onChangeInput === 'function' && onChangeInput(label);
    } else {
      dispatch(
        addAlert(
          'address-search-alert',
          'error',
          'Error',
          'Invalid search query'
        )
      );
    }
    setIsShowResult(false);
  };

  const handleInputChange = (value: string) => {
    if (value) {
      debouncedFunction(value);
    } else {
      dispatch(clearAddressSearchSuggest());
      typeof onChangeInput === 'function' && onChangeInput('');
    }
    setSearchInputValue(value);
  };

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (containerRef.current && evt.target) {
        if (!containerRef.current.contains(evt.target)) {
          setIsShowResult(false);
        }
      }
    },
    [containerRef]
  );

  useEffect(() => {
    defaultInputValue && setSearchInputValue(defaultInputValue);
  }, [defaultInputValue]);

  useComponentMount(() => {
    return () => {
      dispatch(clearAddressSearchSuggest());
      dispatch(removeAlertByGroup('address-search-alert'));
    };
  });

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  return (
    <div ref={containerRef} className={'address-search--wrapper'}>
      {labelText && <label className="bx--label">{labelText}</label>}
      <Search
        id="address-search-input"
        size="lg"
        labelText={labelText}
        placeHolderText="Address search"
        value={searchInputValue}
        onChange={(evt) => handleInputChange(evt.target.value)}
        onBlur={(evt) =>
          typeof onBlurInput === 'function' && onBlurInput(evt.target.value)
        }
      />
      <div className="address-search--searchItems">
        {isShowResult && (
          <ResultsBox items={suggestion} onItemClick={onClickResult} />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({ userCache }: RootState) => ({
  suggestion: userCache.addressSearchSuggestion
});

const connector = connect(mapStateToProps);

type OwnProps = {
  labelText: string;
  defaultInputValue?: string;
  onChangeInput?: Function;
  onBlurInput?: Function;
};

type Props = CFCProps<typeof connector> & OwnProps;

export default connector(AddressSearchPanel);
